import {
  getLocationsForPortfolio,
  orEmptyArray,
} from '@lms/api-client/backend';
import { Button } from '@lms/ui/button';
import { Card, CardContent, CardDescription, CardHeader } from '@lms/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@lms/ui/table';
import {
  LoaderCircle,
  PlusIcon,
  SquareArrowOutUpRightIcon,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import useSWR from 'swr';
import { useTranslations } from 'use-intl';
import {
  PageBox,
  PageButtons,
  PageHeader,
  PageTitle,
} from '@/components/admin/page-box';
import { useCurrentUser } from '@/features/auth/use-auth';
import { useBrand } from '@/features/branding/brand-provider';
import { OverflowMenu } from './overflow-menu';

// Ports app/[brand]/admin/locations/page.tsx (the portfolio list). Data comes
// from SWR instead of the server component's await; delete/add revalidate the
// same key.
export function LocationsList() {
  const t = useTranslations('admin');
  const { brand: site, slug } = useBrand();
  const { user } = useCurrentUser();
  const isSuperAdmin = user?.superAdmin;

  const {
    data: locations,
    isLoading,
    mutate,
  } = useSWR(['admin/locations', site.id], () =>
    orEmptyArray(getLocationsForPortfolio(site.id)),
  );

  return (
    <PageBox>
      <PageHeader>
        <PageTitle>{t('locations')}</PageTitle>
        <PageButtons>
          {isSuperAdmin && (
            <Button asChild>
              <Link to={`/${slug}/admin/locations/add`}>
                <PlusIcon className="size-4" />
                {t('add', { what: t('location').toLowerCase() })}
              </Link>
            </Button>
          )}
        </PageButtons>
      </PageHeader>
      <Card>
        <CardHeader>
          <CardDescription>
            {isSuperAdmin
              ? t('manage', { what: t('locations').toLowerCase() })
              : t('list', { what: t('locations').toLowerCase() })}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-24" />
                <TableHead>{t('name')}</TableHead>
                <TableHead>{t('slug')}</TableHead>
                <TableHead className="w-5" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {locations?.map((l) => (
                <TableRow key={l.id}>
                  <TableCell>
                    <img
                      src={l.logo}
                      alt="logo"
                      className="h-16 max-w-32 object-contain"
                    />
                  </TableCell>
                  <TableCell>
                    <Link to={`/${l.slug}/admin`} className="hover:underline">
                      {l.name}
                    </Link>
                  </TableCell>
                  <TableCell>
                    <Link
                      to={`/${l.slug}/login`}
                      target="_blank"
                      className="hover:underline"
                      title={t('open_slug')}
                    >
                      {l.slug}
                      <SquareArrowOutUpRightIcon className="ml-2 inline-block h-4 w-4" />
                    </Link>
                  </TableCell>
                  <TableCell className="text-right">
                    <OverflowMenu
                      id={l.id}
                      slug={l.slug}
                      onDeleted={() => mutate()}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {isLoading && (
            <div className="flex items-center justify-center py-8">
              <LoaderCircle className="text-primary size-6 animate-spin" />
            </div>
          )}
          {!isLoading && (locations?.length ?? 0) === 0 && (
            <p className="text-muted-foreground py-8 text-center text-sm">
              {t('list', { what: t('locations').toLowerCase() })}
            </p>
          )}
        </CardContent>
      </Card>
    </PageBox>
  );
}
